import React from "react";
import { connect } from "react-redux";
import { currentUserSelector } from "state/currentUser/currentUserSelectors";
import { teamSelector } from "state/entities/entitiesSelectors";
import { currentSubscriptionPlan } from "state/entities/subscriptions/subscriptionsSelectors";
import auth from "lib/auth";
import { startUserSession } from "state/currentUser/currentUserActions";
import { FullLoadingPage } from "views/pages";
// Eager import is bundle-neutral: EditorShell is already in the main chunk
// because Routes statically imports EditorContainer -> EditorContainerLazy,
// which uses EditorShell as its Suspense fallback (a fallback can't itself
// suspend, so it must be eager). It is not behind the lazy editor chunk, so
// importing it here adds no weight to non-editor first load. Do NOT lazy-load
// it here — a Suspense boundary in the boot gate would flash the spinner this
// change exists to remove.
import EditorShell from "views/components/Editor/EditorShell";
import { MOBILE_BREAKPOINT } from "views/components/Editor/mobile/useMobile";
import { MOBILE_EDITOR_ENABLED } from "lib/constants";
import PATHS from "routes/paths";
import {
  fetchTeamIfNeeded,
  fetchTeamSettingsIfNeeded
} from "state/entities/entitiesActions";
import { fetchSubscriptionsIfNeeded } from "../../state/entities/subscriptions/subscriptionsActions";
import { fetchSubscriptionPlansIfNeeded } from "../../state/entities/subscriptionPlans/subscriptionPlansActions";
import { fetchPublicFontsIfNeeded } from "../../state/entities/publicFonts/publicFontsActions";
import { fetchUserTeamFontsIfNeeded } from "../../state/entities/userTeamFonts/userTeamFontsActions";
import { fetchBrandKitFontsIfNeeded } from "../../state/ui/brandKit/BrandKitActions";
import { fetchPersonalTeamIfNeeded } from "state/personalTeam/personalTeamActions";
import { Logger } from "lib";

/* On the editor route, the session-boot gate shows the editor shell instead
   of the generic spinner so the chrome is on screen from first paint —
   matching the same isMobile logic as EditorContainerLazy. */
const isMobileEditorViewport = () =>
  MOBILE_EDITOR_ENABLED &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MOBILE_BREAKPOINT).matches;

export const bootLoadingPage = () =>
  PATHS.isEditorPath(window.location.pathname) ? (
    <EditorShell isMobile={isMobileEditorViewport()} />
  ) : (
    <FullLoadingPage />
  );

const WithUser = WrappedComponent => {
  class WithCurrentUserWrapper extends React.Component {
    componentDidMount() {
      this.props.startUserSession();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      const { currentUser, currentTeam, fetchTeamIfNeeded } = nextProps;

      if (!currentTeam && currentUser.token) {
        fetchTeamIfNeeded();

        const fetchFontsDelay = 5000;
        setTimeout(
          () => nextProps.fetchPublicFontsIfNeeded({ page: 1 }),
          fetchFontsDelay
        );
      }

      if (currentTeam && currentUser.token) {
        nextProps.fetchPersonalTeamIfNeeded({ userId: currentUser.id });
        nextProps.fetchBrandKitFontsIfNeeded(currentTeam.id);
        nextProps.fetchUserTeamFontsIfNeeded({
          teamId: currentTeam.id,
          userId: currentUser.id,
          page: 1
        });
        nextProps.fetchSubscriptionsIfNeeded(currentTeam.id);
        nextProps.fetchSubscriptionPlansIfNeeded();
        nextProps.fetchTeamSettingsIfNeeded({});
      }
    }

    componentDidUpdate() {}

    render() {
      const { currentUser, currentTeam, currentPlan } = this.props;

      if (currentUser.logout) {
        Logger.warn("User Logged out, Push to login");

        window.location = auth.LOGIN_URL;
      }

      if (currentUser.isSwitchingToken) {
        return <FullLoadingPage />;
      }

      if (!currentUser.token || !currentTeam || !currentPlan) {
        return bootLoadingPage();
      }

      return <WrappedComponent {...this.props} />;
    }
  }

  const mapStateToProps = state => ({
    currentUser: currentUserSelector(state),
    currentTeam: teamSelector({ state }),
    currentPlan: currentSubscriptionPlan(state),
    locationPathName: state.router.location.pathName
  });

  const mapDispatchToProps = dispatch => {
    return {
      startUserSession: args => dispatch(startUserSession(args)),
      fetchTeamIfNeeded: args => dispatch(fetchTeamIfNeeded(args)),
      fetchPublicFontsIfNeeded: args =>
        dispatch(fetchPublicFontsIfNeeded(args)),
      fetchBrandKitFontsIfNeeded: teamId =>
        dispatch(fetchBrandKitFontsIfNeeded({ teamId })),
      fetchUserTeamFontsIfNeeded: args =>
        dispatch(fetchUserTeamFontsIfNeeded(args)),
      fetchSubscriptionsIfNeeded: teamId =>
        dispatch(fetchSubscriptionsIfNeeded({ teamId })),
      fetchSubscriptionPlansIfNeeded: args =>
        dispatch(fetchSubscriptionPlansIfNeeded(args)),
      fetchPersonalTeamIfNeeded: args =>
        dispatch(fetchPersonalTeamIfNeeded(args)),
      fetchTeamSettingsIfNeeded: args =>
        dispatch(fetchTeamSettingsIfNeeded(args))
    };
  };

  return connect(mapStateToProps, mapDispatchToProps)(WithCurrentUserWrapper);
};

export default WithUser;
